import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { tokenManager } from './tokenManager';

interface ErrorResponse {
  message: string;
  [key: string]: unknown;
}

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_BACKEND_API_URL || 'https://big-five-gpf7.onrender.com',
  timeout: 10000,
  withCredentials: true, // Enable cookies
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// Request interceptor - Add access token to every request
axiosInstance.interceptors.request.use(
  (config) => {
    const token = tokenManager.accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Check for new tokens in headers
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    // Check for new access token in response headers
    const newAccessToken = response.headers['x-new-access-token'];
    if (newAccessToken && typeof newAccessToken === 'string') {
      console.log('🔄 Found new access token in response header, updating...');
      tokenManager.updateToken(newAccessToken);
    }
    
    return response;
  },
  async (error: AxiosError<ErrorResponse>) => {
    // Handle 401 errors by clearing tokens (let the view handle authentication)
    if (error.response?.status === 401) {
      console.log('❌ Request failed with 401, clearing tokens');
      tokenManager.clearTokens();
    }
    return Promise.reject(error);
  }
);

// API client methods
export const api = {
  get: <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    axiosInstance
      .get(url, config)
      .then((response: AxiosResponse<T>) => response.data),

  post: <T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> =>
    axiosInstance
      .post(url, data, config)
      .then((response: AxiosResponse<T>) => response.data),

  put: <T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> =>
    axiosInstance
      .put(url, data, config)
      .then((response: AxiosResponse<T>) => response.data),

  patch: <T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> =>
    axiosInstance
      .patch(url, data, config)
      .then((response: AxiosResponse<T>) => response.data),

  delete: <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    axiosInstance
      .delete(url, config)
      .then((response: AxiosResponse<T>) => response.data),
};
